import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { createCanonicalKanbanViewData } from '@streamlain/database-identity/database-view-defaults';

import type { GroupBy, GroupProperty } from '../../core/common/types.js';
import type { FilterGroup } from '../../core/filter/types.js';
import type { Sort } from '../../core/sort/types.js';
import { type BasicViewDataType, viewType } from '../../core/view/data-view.js';
import {
  createKanbanL1GroupBy,
  getKanbanL1GroupableColumnIds,
} from './groupable.js';
import { KanbanSingleView } from './kanban-view-manager.js';
import { orderKanbanPropertyIdsForDefault } from './property-defaults.js';

export const kanbanViewType = viewType('kanban');

export type KanbanViewColumn = {
  id: string;
  hide?: boolean;
};

type DataType = {
  columns: KanbanViewColumn[];
  filter: FilterGroup;
  groupBy: GroupBy;
  groupBy2?: GroupBy;
  sort?: Sort;
  header: {
    titleColumn?: string;
    iconColumn?: string;
    coverColumn?: string;
  };
  groupProperties: GroupProperty[];
  groupProperties2: GroupProperty[];
  groupCollapsed2: string[];
  columnWidth?: number;
};
export type KanbanViewData = BasicViewDataType<
  typeof kanbanViewType.type,
  DataType
>;
export const kanbanViewModel = kanbanViewType.createModel<KanbanViewData>({
  defaultName: 'Kanban View',
  dataViewManager: KanbanSingleView,
  defaultData: viewManager => {
    const sourceColumnIds =
      viewManager.dataSource.getBoundedPropertyIdsWindow().propertyIds;
    const columns = orderKanbanPropertyIdsForDefault(
      viewManager.dataSource,
      sourceColumnIds
    );
    const sorted = getKanbanL1GroupableColumnIds(viewManager.dataSource);
    const l1ColumnId = sorted[0];
    if (!l1ColumnId) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        'no groupable column found for Kanban L1'
      );
    }
    const l1Type = viewManager.dataSource.propertyTypeGet(l1ColumnId);
    const l1Meta = l1Type && viewManager.dataSource.propertyMetaGet(l1Type);
    const l1Data = viewManager.dataSource.propertyDataGet(l1ColumnId);
    if (!l1Meta || !l1Data) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        'kanban default data: property metadata missing'
      );
    }
    const groupBy = createKanbanL1GroupBy(viewManager.dataSource, l1ColumnId);
    if (!groupBy) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        'kanban default data: defaultGroupBy returned undefined'
      );
    }
    return createCanonicalKanbanViewData({
      properties: columns.map(id => ({
        id,
        type: viewManager.dataSource.propertyTypeGet(id) ?? '',
      })),
      groupBy,
    });
  },
});
